import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import {
  ComputedTable,
  computedTableDownloadUrl,
  deleteComputedTable,
  listComputedTables,
  refreshComputedTable,
} from "@/lib/api/computedTables";
import { Stack } from "@/components/common/Stack";
import { Collapsible } from "@/components/common/Collapsible";
import { SectionRule } from "components/common/SectionRule";
import { ComputedTableRow } from "@/components/main/overview/ComputedTableRow";
import { ConfirmDialog } from "@/components/main/utils/ConfirmDialog";
import { ExportToSheetsDialog } from "@/components/canvas/detailsSide/tableDetails/ExportToSheets/ExportToSheetsDialog";
import { showNotificationGlobal } from "@/stores/useNotificationStore";
import { useOnlineStatus } from "@/components/hooks/useOnlineStatus";
import {
  pollingAllowed,
  useResumeRefresh,
} from "@/components/hooks/activityGate";

/// Polls ONLY while the section is expanded — opening it is a declaration of
/// attention (watching for MCP-created tables), so that's when freshness
/// matters. Collapsed: no polling; expanding refetches.
const OPEN_POLL_INTERVAL_MS = 10_000;

/// Overview section listing the user's own computed tables (saved via the MCP
/// `create_table_from_sql` tool), newest-first. Not shareable; each row
/// exports / refreshes / deletes.
export const ComputedTablesSection: React.FC = () => {
  const [tables, setTables] = useState<ComputedTable[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteTarget, setDeleteTarget] = useState<ComputedTable | null>(null);
  const [exportTarget, setExportTarget] = useState<ComputedTable | null>(null);
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [searchParams, setSearchParams] = useSearchParams();
  // Collapsed by default: the list grows on its own as the MCP saves tables, and
  // it sits under the models you came here for. A `?computed=<id>` deep link
  // opens it from the first render — opening it later would leave the scroll
  // below aiming at a row that still has no height.
  const [open, setOpen] = useState(() => searchParams.get("computed") !== null);
  const isOnline = useOnlineStatus();

  const rowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [highlightedId, setHighlightedId] = useState<string | null>(null);

  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  const refetch = useCallback(async () => {
    try {
      setTables(await listComputedTables());
    } catch {
      // Best-effort: leave the previous list on transient failure.
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refetch();
  }, [refetch]);

  useEffect(() => {
    if (!open) return;
    void refetch();
    const interval = window.setInterval(() => {
      if (!navigator.onLine || !pollingAllowed()) return;
      void refetch();
    }, OPEN_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [open, refetch]);

  useResumeRefresh(
    useCallback(() => {
      if (open && navigator.onLine) void refetch();
    }, [open, refetch]),
  );

  const targetId = searchParams.get("computed");
  useEffect(() => {
    if (!targetId) return;
    if (!tables.some((t) => t.computed_table_id === targetId)) return;
    const el = rowRefs.current.get(targetId);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    setHighlightedId(targetId);
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete("computed");
        return next;
      },
      { replace: true },
    );
    const timer = window.setTimeout(() => setHighlightedId(null), 2500);
    return () => clearTimeout(timer);
  }, [targetId, tables, setSearchParams]);

  const prevOnlineRef = useRef(isOnline);
  useEffect(() => {
    const wasOnline = prevOnlineRef.current;
    prevOnlineRef.current = isOnline;
    if (isOnline && !wasOnline) {
      void refetch();
    }
  }, [isOnline, refetch]);

  const onRefresh = useCallback(
    async (table: ComputedTable) => {
      setRefreshingId(table.computed_table_id);
      try {
        await refreshComputedTable(table.computed_table_id);
        showNotificationGlobal(`Refreshed "${table.name}"`, "success");
        await refetch();
      } catch (e) {
        const reason =
          (e as { response?: { data?: { reason?: string } } })?.response?.data
            ?.reason ?? "Failed to refresh computed table";
        showNotificationGlobal(reason, "error");
      } finally {
        setRefreshingId(null);
      }
    },
    [refetch],
  );

  const onDownload = useCallback(
    (table: ComputedTable, format: "csv" | "parquet" = "csv") => {
      const a = document.createElement("a");
      a.href = computedTableDownloadUrl(table.computed_table_id, format);
      document.body.appendChild(a);
      a.click();
      a.remove();
    },
    [],
  );

  const onConfirmDelete = useCallback(async () => {
    const target = deleteTarget;
    setDeleteTarget(null);
    if (!target) return;
    try {
      await deleteComputedTable(target.computed_table_id);
      showNotificationGlobal(`Deleted "${target.name}"`, "success");
      await refetch();
    } catch {
      showNotificationGlobal("Failed to delete computed table", "error");
    }
  }, [deleteTarget, refetch]);

  // Hidden entirely (heading included) while loading or empty — no flash of a
  // bare heading.
  if (loading || tables.length === 0) {
    return null;
  }

  const staleCount = tables.filter((t) => t.stale).length;

  return (
    <div className="mt-12">
      <SectionRule
        inset
        icon={<DashboardOutlinedIcon sx={{ fontSize: 12 }} />}
        label={`Computed tables · ${tables.length}`}
        action={{
          label: open
            ? "Hide"
            : staleCount > 0
              ? `Show · ${staleCount} outdated`
              : "Show",
          onClick: () => setOpen((v) => !v),
          testId: "toggle-computed-tables",
        }}
      />
      <Collapsible open={open}>
        <Stack gap="2xs">
          {tables.map((t) => (
            <ComputedTableRow
              key={t.computed_table_id}
              table={t}
              refreshing={refreshingId === t.computed_table_id}
              expanded={expandedIds.has(t.computed_table_id)}
              highlighted={highlightedId === t.computed_table_id}
              onToggleExpanded={() => toggleExpanded(t.computed_table_id)}
              onRefresh={() => onRefresh(t)}
              onDownload={(format) => onDownload(t, format)}
              onExportSheets={() => setExportTarget(t)}
              onDelete={() => setDeleteTarget(t)}
              registerRef={(el) => {
                if (el) rowRefs.current.set(t.computed_table_id, el);
                else rowRefs.current.delete(t.computed_table_id);
              }}
            />
          ))}
        </Stack>
      </Collapsible>

      {exportTarget && (
        <ExportToSheetsDialog
          open
          onClose={() => setExportTarget(null)}
          source={{
            kind: "table",
            table_id: exportTarget.computed_table_id,
            version: 0,
          }}
          title={exportTarget.name}
        />
      )}

      <ConfirmDialog
        open={deleteTarget !== null}
        title="Delete computed table"
        message={
          <Stack gap="xs">
            <p>
              Permanently delete{" "}
              <span className="text-fg-bold">{deleteTarget?.name}</span>?
            </p>
            <p className="text-small text-fg-muted">
              Models that imported it will lose access to its data.
            </p>
          </Stack>
        }
        confirmText="Delete"
        confirmVariant="destructive"
        onConfirm={() => void onConfirmDelete()}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
};
